import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from 'react-intl';
import {useTheme} from '@mui/material/styles';
import {countries} from 'wobi-web-common';
import FlagBadge, {CheckBadge} from '../FlagBadge';
import {Dialog, Input, Button} from 'wobi-web-common/dist/v2/index.js';

/**
 * AllDestinationsModal — Full-screen searchable multi-select modal.
 * Shows all available countries from web-common/countries with flag badges.
 *
 * Props:
 * - open: boolean
 * - selected: string[] — currently selected country values
 * - onToggle(countryValue): called when user toggles a country
 * - onClose(): called to close the modal
 */
const AllDestinationsModal = ({open, selected, onToggle, onClose}) => {
  const intl = useIntl();
  const theme = useTheme();
  const [query, setQuery] = React.useState('');

  React.useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const trimmed = query.trim();
  const filtered = trimmed
    ? countries.filter((c) => c.label.indexOf(trimmed) !== -1)
    : countries;

  const count = selected.length;
  const handleCountrySelect = (countryValue) => {
    onToggle(countryValue);
    onClose();
  };

  const footer = (
    <div
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <span style={{fontSize: theme.typography.pxToRem(13.5), fontWeight: theme.typography.fontWeightSemiBold, color: theme.palette.text.secondary}}>
        {count > 0 ? intl.formatMessage({id: 'allDestinationsModal.selectedCount'}, {count}) :
          <FormattedMessage id='allDestinationsModal.noSelection'/>}
      </span>
      <Button variant='primary' onClick={onClose} disabled={count === 0} style={{height: 46, padding: '0 30px', fontSize: theme.typography.pxToRem(16)}}>
        <FormattedMessage id='allDestinationsModal.done'/>
      </Button>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={intl.formatMessage({id: 'step1.tab1.targetTitle'})}
      ariaLabel={intl.formatMessage({id: 'allDestinationsModal.ariaLabel'})}
      width={680}
      footer={footer}
      style={{maxHeight: '86vh', overflowY: 'auto', textAlign: 'right'}}
    >
      <p style={{margin: '0 0 14px', fontSize: theme.typography.pxToRem(13.5), color: theme.palette.text.secondary}}>
        <FormattedMessage id='allDestinationsModal.subtitle'/>
      </p>

        {/* Search */}
        <div style={{margin: '16px 0 14px'}}>
          <Input
            size='lg'
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={intl.formatMessage({id: 'allDestinationsModal.searchPlaceholder'})}
            style={{height: 50, borderRadius: 'var(--wobi-v2-radius-lg)'}}
            inputStyle={{textAlign: 'right', fontSize: theme.typography.pxToRem(15.5)}}
            iconStart={
              <svg
                width={18} height={18} viewBox='0 0 24 24'
                fill='none' stroke='currentColor' strokeWidth={2}
                strokeLinecap='round' strokeLinejoin='round'
              >
                <path d='M21 21l-4.35-4.35' />
                <path d='M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z' />
              </svg>
            }
          />
        </div>

        {/* Grid */}
        <div style={{flex: 1, overflowY: 'auto', paddingBottom: 12}}>
          {filtered.length === 0 && (
            <div style={{padding: 26, fontSize: theme.typography.pxToRem(15), color: theme.palette.text.secondary, textAlign: 'center'}}>
              <FormattedMessage id='allDestinationsModal.noMatch'/>
            </div>
          )}
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8}}>
            {filtered.map((c) => {
              const isOn = selected.includes(c.value);
              return (
                <button
                  key={c.value}
                  type='button'
                  onClick={() => handleCountrySelect(c.value)}
                  aria-pressed={isOn}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 11,
                    textAlign: 'right',
                    padding: '10px 12px',
                    cursor: 'pointer',
                    background: isOn ? theme.palette.primary.lighter : theme.palette.background.paper,
                    border: `1.5px solid ${isOn ? theme.palette.primary.main : theme.palette.divider}`,
                    borderRadius: 'var(--wobi-v2-radius-lg)',
                    fontFamily: 'inherit',
                    transition: 'border-color 120ms ease-out, background 120ms ease-out',
                  }}
                >
                  <span style={{position: 'relative', display: 'inline-flex', flex: 'none'}}>
                    <FlagBadge src={c.imgSrc} size={34} ringW={1.5} selected={isOn} />
                    {isOn && <CheckBadge size={16} />}
                  </span>
                  <span style={{
                    fontSize: theme.typography.pxToRem(14.5),
                    fontWeight: isOn ? theme.typography.fontWeightBold : theme.typography.fontWeightMedium,
                    color: isOn ? theme.palette.primary.dark : theme.palette.text.primary,
                    lineHeight: 1.2,
                  }}>
                    {c.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

    </Dialog>
  );
};

AllDestinationsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  selected: PropTypes.array.isRequired,
};

export default AllDestinationsModal;
